import { useState } from "react";
import { useContent } from "../../i18n";
import { formatNumber } from "./format";
import "./style.scss";

type Mode = "manual" | "api" | "auto";
type StepKey =
  | "upload"
  | "uploadUninsured"
  | "manual"
  | "api"
  | "auto"
  | "autoFail"
  | "autoFix"
  | "approve"
  | "reject"
  | "confirm"
  | "pay"
  | "visit"
  | "result";

// The path a request takes, given the switches. Mirrors the flow agreed for providers:
// the provider submits to the insurer (manually now; by integration or automation only where the insurer agrees).
const buildPath = (insured: boolean, mode: Mode, autoFails: boolean, approved: boolean): StepKey[] => {
  if (!insured) return ["uploadUninsured", "confirm", "pay", "visit", "result"];
  const submit: StepKey[] =
    mode === "auto" ? (autoFails ? ["auto", "autoFail", "autoFix"] : ["auto"]) : [mode];
  if (!approved) return ["upload", ...submit, "reject"];
  return ["upload", ...submit, "approve", "pay", "visit", "result"];
};

export const RequestDemo = () => {
  const { demos } = useContent();
  const t = demos.request;

  const [insured, setInsured] = useState(true);
  const [mode, setMode] = useState<Mode>("manual");
  const [autoFails, setAutoFails] = useState(false);
  const [approved, setApproved] = useState(true);
  const [done, setDone] = useState(0);

  const path = buildPath(insured, mode, autoFails, approved);
  const current = done > 0 ? path[done - 1] : null;
  const finished = done === path.length;
  const visits = path.slice(0, done).filter((key) => key === "visit").length;
  const alert = current === "autoFail";

  // Changing a switch starts the request again, so every path is seen from its first step.
  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setDone(0);
  };

  return (
    <div className="demo">
      <div className="demo-stage request-stage">
        <div className="panel phone-panel">
          <p className="panel-title">{t.phoneTitle}</p>
          <div className="phone">
            <div className="phone-bar">MedHub</div>
            <p className="phone-service">{t.service}</p>
            <p className={current ? "phone-status live" : "phone-status"}>
              {current ? t.steps[current].phone : t.waiting}
            </p>
            <ol className="phone-track">
              {path.map((key, i) => (
                <li key={key} className={i < done ? "passed" : ""} />
              ))}
            </ol>
          </div>
        </div>

        <div className={alert ? "panel desk-panel alert" : "panel desk-panel"}>
          <p className="panel-title">{t.deskTitle}</p>
          <div className="desk">
            <p className="desk-file-title">
              {t.fileTitle} — {t.service}
            </p>
            <ul className="desk-file">
              <li className={done > 0 ? "ok" : ""}>{t.fileItems.prescription}</li>
              {insured && <li className={done > 0 ? "ok" : ""}>{t.fileItems.card}</li>}
              <li className={done > 0 ? "ok" : ""}>{t.fileItems.details}</li>
              <li className={done > 0 ? "ok" : ""}>{t.fileItems.medical}</li>
            </ul>
            <p className="desk-status">{current ? t.steps[current].desk : "—"}</p>
          </div>
        </div>

        <ol className="demo-log">
          {path.slice(0, done).map((key) => (
            <li key={key}>{t.steps[key].log}</li>
          ))}
        </ol>

        <div className="counters">
          <div>
            <b>{formatNumber(visits)}</b>
            <span>{t.visits}</span>
          </div>
          <div>
            <b>{formatNumber(0)}</b>
            <span>{t.queues}</span>
          </div>
          <p>{t.today}</p>
        </div>
      </div>

      <div className="demo-controls">
        <button className="act" type="button" disabled={finished} onClick={() => setDone(done + 1)}>
          {demos.common.next}
        </button>
        <button className="quiet" type="button" onClick={() => setDone(0)}>
          {demos.common.restart}
        </button>

        <div className="switches">
          <label className="check">
            <input type="checkbox" checked={insured} onChange={(e) => reset(setInsured)(e.target.checked)} />
            <span>{t.insured}</span>
          </label>

          {insured && (
            <>
              <fieldset>
                <legend>{t.modeLabel}</legend>
                {(Object.keys(t.modes) as Mode[]).map((key) => (
                  <label className="radio" key={key}>
                    <input
                      type="radio"
                      name="submit-mode"
                      checked={mode === key}
                      onChange={() => reset(setMode)(key)}
                    />
                    <span>{t.modes[key]}</span>
                  </label>
                ))}
              </fieldset>

              {mode === "auto" && (
                <label className="check">
                  <input
                    type="checkbox"
                    checked={autoFails}
                    onChange={(e) => reset(setAutoFails)(e.target.checked)}
                  />
                  <span>{t.autoFails}</span>
                </label>
              )}

              <fieldset>
                <legend>{t.decisionLabel}</legend>
                <label className="radio">
                  <input type="radio" name="decision" checked={approved} onChange={() => reset(setApproved)(true)} />
                  <span>{t.approve}</span>
                </label>
                <label className="radio">
                  <input type="radio" name="decision" checked={!approved} onChange={() => reset(setApproved)(false)} />
                  <span>{t.reject}</span>
                </label>
              </fieldset>
            </>
          )}
        </div>

        <p className="demo-note">{demos.common.note}</p>
      </div>
    </div>
  );
};
